import { useTranslation } from 'react-i18next'
import { Link, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { Footer } from '@/components/layout/Footer'
import { Plane, Users, Wrench, Shield } from 'lucide-react'

export function LandingPage() {
  const { t } = useTranslation()
  const { isAuthenticated, isLoading } = useAuthStore()

  // Redirect authenticated users to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700 mx-auto mb-4"></div>
          <p className="text-slate-600">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-700 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">FJ</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Flight Journal</h1>
              <p className="text-xs text-slate-500">{t('auth.marketingSubtitle')}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Link
              to="/auth/login"
              className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors"
            >
              {t('auth.login')}
            </Link>
            <Link
              to="/auth/register"
              className="px-4 py-2 text-sm font-medium bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors"
            >
              {t('landing.cta.register')}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-700 to-primary-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t('landing.hero.title')}
          </h2>
          <p className="text-xl md:text-2xl text-primary-100 mb-8 max-w-3xl mx-auto">
            {t('landing.hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/auth/register"
              className="px-8 py-3 bg-white text-primary-700 font-semibold rounded-lg hover:bg-primary-50 transition-colors"
            >
              {t('landing.hero.getStarted')}
            </Link>
            <Link
              to="/about"
              className="px-8 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-500 transition-colors"
            >
              {t('landing.hero.learnMore')}
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h3 className="text-3xl font-bold text-center text-slate-900 mb-12">
            {t('landing.features.title')}
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Operators */}
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-primary-700" />
              </div>
              <h4 className="text-xl font-semibold text-slate-900 mb-3">
                {t('landing.features.operators.title')}
              </h4>
              <p className="text-slate-600">
                {t('landing.features.operators.description')}
              </p>
            </div>

            {/* Pilots */}
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Plane className="w-8 h-8 text-primary-700" />
              </div>
              <h4 className="text-xl font-semibold text-slate-900 mb-3">
                {t('landing.features.pilots.title')}
              </h4>
              <p className="text-slate-600">
                {t('landing.features.pilots.description')}
              </p>
            </div>

            {/* Maintenance */}
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Wrench className="w-8 h-8 text-primary-700" />
              </div>
              <h4 className="text-xl font-semibold text-slate-900 mb-3">
                {t('landing.features.maintenance.title')}
              </h4>
              <p className="text-slate-600">
                {t('landing.features.maintenance.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-10 h-10 text-green-700" />
          </div>
          <h3 className="text-3xl font-bold text-slate-900 mb-4">
            {t('landing.compliance.title')}
          </h3>
          <p className="text-lg text-slate-600">
            {t('landing.compliance.description')}
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-3xl font-bold text-slate-900 mb-4">
            {t('landing.cta.title')}
          </h3>
          <p className="text-lg text-slate-600 mb-8">
            {t('landing.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/auth/register"
              className="px-8 py-3 bg-primary-700 text-white font-semibold rounded-lg hover:bg-primary-800 transition-colors"
            >
              {t('landing.cta.register')}
            </Link>
            <Link
              to="/auth/login"
              className="px-8 py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-colors"
            >
              {t('landing.cta.login')}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
